import { useFormContext } from 'react-hook-form';
import { useUpdatePasswordByAdvertiserUserMutation } from '@anytag/graphql';
import { Button, ButtonSubmit, Form } from '@anytag/shared-atoms';
import { useAllAvailableCountryOptions, useQueryHelper, useToggleState } from '@anytag/shared-hooks';
import {
  TextField,
  PasswordField,
  SelectorField,
  SwitchToggleField,
  ChangePasswordFormModal,
  ChangePasswordFormValues
} from '@anytag/shared-molecules';
import { cn, getSubscriptionTypeLabel } from '@anytag/shared-utils';
import { FormAction } from '@anytag/shared-types';
import { SettingsFormKeys, SettingsFormValues } from './types';

export const Template = () => {
  const modalState = useToggleState();
  const { t, enqueueSnackbar } = useQueryHelper();

  const { allAvailableCountryOptions } = useAllAvailableCountryOptions();
  const { loading, callUpdatePasswordByAdvertiserUser } = useUpdatePasswordByAdvertiserUserMutation();
  const {
    formState: { isSubmitting },
    getValues
  } = useFormContext<SettingsFormValues>();
  const planTitle = getSubscriptionTypeLabel(getValues().planType);

  const handleChangePassword: FormAction<ChangePasswordFormValues>['onSubmit'] = async ({
    password,
    currentPassword
  }) => {
    try {
      await callUpdatePasswordByAdvertiserUser({
        variables: { input: { newPassword: password, originalPassword: currentPassword } }
      });
      enqueueSnackbar(t('New Account Password was saved successfully'), { variant: 'success' });
      modalState.close();
    } catch (error) {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    }
  };

  return (
    <>
      <Form.Body className="form-container">
        <Form.SectionTitle label={t('Company Information')} />

        <TextField<SettingsFormKeys>
          name="advertiserName"
          placeholder="AnyMindgroup"
          title={t('TextForm.Company Name')}
          required
        />

        <TextField<SettingsFormKeys>
          name="advertiserCompanyUrl"
          title={t('TextForm.Company URL')}
          placeholder="https://anytag.tech"
          required
        />

        <Form.Fields>
          <TextField<SettingsFormKeys>
            placeholder="03-1111-1111"
            name="advertiserPhoneNumber"
            title={t('TextForm.Phone Number')}
            required
          />

          <SelectorField<SettingsFormKeys>
            name="advertiserCountryId"
            title={t('Selector.Country')}
            options={allAvailableCountryOptions}
            required
          />
        </Form.Fields>

        <Form.SectionTitle className="mt-10" label={t('User Information')} />

        <TextField<SettingsFormKeys> name="name" title={t('TextForm.Name')} placeholder="Naoya Kurita" required />

        <Form.Fields>
          <TextField<SettingsFormKeys>
            name="email"
            placeholder="myemail@gmail.com"
            title={t('TextForm.Email Address')}
            required
          />

          <SelectorField<SettingsFormKeys>
            name="countryId"
            title={t('Selector.Country')}
            options={allAvailableCountryOptions}
            required
          />
        </Form.Fields>

        <Form.Fields className="w-95 items-end">
          <PasswordField<SettingsFormKeys>
            name="password"
            title={t('TextForm.Password')}
            placeholder={t<string>('Password.Your Password')}
            required
            disabled
          />

          <Form.Field className="flex-none">
            <Button
              variant="outline"
              onClick={modalState.open}
              title={t('Button.Change Password')}
              className={cn('px-6 uppercase', { 'opacity-0': modalState.status })}
            />
          </Form.Field>
        </Form.Fields>

        <SwitchToggleField<SettingsFormKeys>
          name="notificationSetting"
          title={t('TextForm.Notification Settings')}
          help={t('Tooltip.Notification for Advertiser settings help')}
        />

        <Form.SectionTitle label={t('Account Manager Plan')} className="mb-0" />

        {planTitle ? (
          <span className="mx-auto text-lg font-semibold">{t(planTitle)}</span>
        ) : (
          <span className="text-base font-normal">{t('No plan subscription')}</span>
        )}
        <p className="text-sm font-normal text-tertiary">{t('Please contact our staff to upgrade subscriptions')}</p>
      </Form.Body>

      <Form.Footer>
        <ButtonSubmit title={t('Button.Save')} loading={{ status: isSubmitting, title: t('Button.Saving') }} />
      </Form.Footer>

      <ChangePasswordFormModal
        loading={loading}
        open={modalState.status}
        onClose={modalState.close}
        onSubmit={handleChangePassword}
      />
    </>
  );
};
